import {
  resolvePanelContentRect,
  resolveVerticalStack,
  type Rect,
  type PanelContentSpec,
  type VerticalStackSpec,
} from "@yjsgames/phaser-ui/panel-content";
import battleTabSpec from "../data/layouts/battleTab.json";
import { MODULE_GRID, LAYOUT } from "../data/balance.js";

/** 화면 레이아웃 좌표 중 battleTabLayout이 필요한 필드만 명시 — ShellLayout과 구조적 호환. */
interface LayoutSubset {
  readonly frameInsetX: number;
  readonly tabPanelTop: number;
  readonly tabPanelHeight: number;
}

// 탭 콘텐츠 패널 inset 스펙 — 순수 레이아웃 SSoT(여기). uiTokens(CONTENT_PANEL_STYLE)는 이 값을 import해
// 9-slice 렌더에 쓴다(순수 → phaser 방향, 역참조 금지). battleTabLayout이 node-pure를 유지해야 단위테스트 가능.
/** 탭 콘텐츠 9-slice 보더 두께(px). tab_content_panel_9slice 에셋과 한 쌍. */
export const TAB_CONTENT_BORDER = { left: 10, top: 10, right: 10, bottom: 10 };
/** 탭 콘텐츠 보더 안쪽 추가 패딩(px). */
export const TAB_CONTENT_PADDING = 6;
/** 탭 콘텐츠 가로 추가 여백(resolvePanelContentRect innerPadX). */
export const TAB_INNER_PADDING_X = 8;
/** 탭 콘텐츠 세로 추가 여백(innerPadY). 세로가 빡빡해 padX보다 작게 둬 팔레트 가시 높이 보존. */
export const TAB_INNER_PADDING_Y = 2;

// 팔레트 타일 메트릭 — 순수 SSoT(여기). ModulePaletteView(렌더)가 import해 타일을 배치하고,
// 튜토리얼 손가락 드래그 출발점(paletteFirstCell)도 같은 값에서 파생한다(스크롤 패널 중심이 아닌 첫 셀).
/** 팔레트 타일 폭(px). */
export const PALETTE_TILE_W = 84;
/** 팔레트 타일 높이(px). 세로 footprint(2줄 심볼) + 이름 줄 수용. */
export const PALETTE_TILE_H = 46;
/** 팔레트 타일 간격(px). */
export const PALETTE_TILE_GAP = 6;
/** 팔레트 헤더("보유 모듈") 높이(px) — 스크롤 콘텐츠는 이 아래에서 시작. */
export const PALETTE_HEADER_H = 18;

/** 전투 탭 콘텐츠 요소의 화면 좌표 (BattleTabView가 이 결과로 배치). */
export interface BattleTabLayout {
  /** width = 콘텐츠 폭 — 경로 텍스트 마키 가로 클립 뷰포트(MarqueeText). */
  readonly pathText: { x: number; y: number; width: number };
  readonly specText: { x: number; y: number };
  /** 발동 중 시너지 칩 전용 행 — spec/path 텍스트와 같은 줄에 두지 않아 겹침을 막는다. */
  readonly synergyRow: { x: number; y: number; width: number; height: number };
  /** 그리드 페이지 컨트롤 전용 행 — sort row와 분리해 도구 버튼처럼 보이지 않게 한다. */
  readonly gridPager: { x: number; y: number; width: number; height: number };
  /** 그리드 좌상단 원점 + 폭/높이. size는 기존 consumer 호환용 width. */
  readonly grid: { originX: number; originY: number; size: number; height: number };
  /** 정렬 필터 전용 행(그리드↔팔레트 사이) — 텍스트 없는 전용 영역이라 필터버튼이 어떤 텍스트와도 겹치지 않는다(정적검증 대상). */
  readonly sort: { x: number; y: number; width: number; height: number };
  readonly palette: { x: number; y: number; width: number; height: number };
  /** 팔레트 첫 타일 셀 rect(스크롤 미이동 기준) — 튜토리얼 드래그 출발점이 스크롤 중심이 아닌 모듈 셀이 되게. */
  readonly paletteFirstCell: { x: number; y: number; width: number; height: number };
}

// 요소 배치(순서·gap·정렬)는 data/layouts/battleTab.json에 선언 — 코드 수정·재컴파일 없이 조정.
// json import는 align을 string으로 추론하므로 선언 union(VerticalStackSpec)으로 좁힌다.
// 그리드 박스 크기는 json에 두지 않고 MODULE_GRID(cellSize/visibleRows) 단일 SSoT에서 파생 주입한다.
// visible 높이는 전투 탭 최대 표시 기준(7열 x 4행)을 따른다.
const GRID_PX =
  MODULE_GRID.width * (MODULE_GRID.cellSize + MODULE_GRID.cellGap) - MODULE_GRID.cellGap;
const GRID_VISIBLE_PX =
  MODULE_GRID.visibleRows * (MODULE_GRID.cellSize + MODULE_GRID.cellGap) - MODULE_GRID.cellGap;
const BATTLE_TAB_SPEC: VerticalStackSpec = {
  children: (battleTabSpec as unknown as VerticalStackSpec).children.map((c) =>
    c.id === "grid" ? { ...c, width: GRID_PX, height: GRID_VISIBLE_PX } : c,
  ),
};

/**
 * 전투 탭 콘텐츠 좌표 계산 (Phaser 비의존 순수 함수).
 * 프레임 inset은 `resolvePanelContentRect`, 그 안의 요소 세로 분배는 데이터 선언(`battleTab.json`)을
 * `resolveVerticalStack`으로 해석 — 둘 다 `@yjsgames/phaser-ui` 공통 계약. battle 전용 좌표 산술 0.
 */
export function computeBattleTabLayout(panelRect: Rect, spec: PanelContentSpec): BattleTabLayout {
  const content = resolvePanelContentRect(panelRect, spec);
  const r = resolveVerticalStack(BATTLE_TAB_SPEC, content);
  const { pathText, specText, synergyRow, gridPager, grid, sortRow, palette } = r;
  if (!pathText || !specText || !synergyRow || !gridPager || !grid || !sortRow || !palette) {
    throw new Error("battleTab.json: pathText/specText/synergyRow/gridPager/grid/sortRow/palette id가 모두 필요합니다");
  }
  return {
    pathText: { x: pathText.x, y: pathText.y, width: pathText.width },
    specText: { x: specText.x, y: specText.y },
    synergyRow: { x: synergyRow.x, y: synergyRow.y, width: synergyRow.width, height: synergyRow.height },
    gridPager: { x: gridPager.x, y: gridPager.y, width: gridPager.width, height: gridPager.height },
    grid: { originX: grid.x, originY: grid.y, size: GRID_PX, height: GRID_VISIBLE_PX },
    sort: { x: sortRow.x, y: sortRow.y, width: sortRow.width, height: sortRow.height },
    palette: { x: palette.x, y: palette.y, width: palette.width, height: palette.height },
    // 첫 타일(col0,row0): 헤더 아래 시작 + 타일 폭/높이. ModulePaletteView.update의 lx/ly 산식과 동형.
    paletteFirstCell: {
      x: palette.x,
      y: palette.y + PALETTE_HEADER_H,
      width: PALETTE_TILE_W,
      height: PALETTE_TILE_H,
    },
  };
}

/** 이미 ShellFrame이 계산한 탭 콘텐츠 rect 안에서 전투 탭 요소를 배치한다. */
export function computeBattleTabLayoutInContentRect(content: Rect): BattleTabLayout {
  const r = resolveVerticalStack(BATTLE_TAB_SPEC, content);
  const { pathText, specText, synergyRow, gridPager, grid, sortRow, palette } = r;
  if (!pathText || !specText || !synergyRow || !gridPager || !grid || !sortRow || !palette) {
    throw new Error("battleTab.json: pathText/specText/synergyRow/gridPager/grid/sortRow/palette id가 모두 필요합니다");
  }
  return {
    pathText: { x: pathText.x, y: pathText.y, width: pathText.width },
    specText: { x: specText.x, y: specText.y },
    synergyRow: { x: synergyRow.x, y: synergyRow.y, width: synergyRow.width, height: synergyRow.height },
    gridPager: { x: gridPager.x, y: gridPager.y, width: gridPager.width, height: gridPager.height },
    grid: { originX: grid.x, originY: grid.y, size: GRID_PX, height: GRID_VISIBLE_PX },
    sort: { x: sortRow.x, y: sortRow.y, width: sortRow.width, height: sortRow.height },
    palette: { x: palette.x, y: palette.y, width: palette.width, height: palette.height },
    paletteFirstCell: {
      x: palette.x,
      y: palette.y + PALETTE_HEADER_H,
      width: PALETTE_TILE_W,
      height: PALETTE_TILE_H,
    },
  };
}

/**
 * 전투 탭 패널 inset(cx/cw) + 콘텐츠 spec의 SSoT. 화면 폭만 받아 `computeBattleTabLayout`을 호출한다.
 * BattleTabView(콘텐츠 배치)·battleBoot·GameTargetResolver(튜토리얼 그리드/팔레트 하이라이트)가 공유 —
 * frameInsetX·보더·innerPad 좌표가 한 곳에서만 정의되게 한다(중복 재유입 차단). 순수 함수(phaser 비의존).
 */
export function computeBattleTabLayoutForScreen(
  screenWidth: number,
  layout: LayoutSubset = LAYOUT,
  expandedTop?: number,
): BattleTabLayout {
  const cx = layout.frameInsetX;
  const cw = screenWidth - layout.frameInsetX * 2;
  // 펼치기: 패널 top을 expandedTop으로 끌어올리고 height를 패널 하단(tabPanelTop+tabPanelHeight)까지 확장
  // (그리드↑·팔레트 fill↑). tabPanelBottom은 tabPanelTop+tabPanelHeight 파생이라 별도 필드 불요.
  const top = expandedTop ?? layout.tabPanelTop;
  const panelBottom = layout.tabPanelTop + layout.tabPanelHeight;
  const height = expandedTop !== undefined ? panelBottom - expandedTop : layout.tabPanelHeight;
  return computeBattleTabLayout(
    { x: cx, y: top, width: cw, height },
    {
      border: TAB_CONTENT_BORDER,
      contentPadding: TAB_CONTENT_PADDING,
      innerPadX: TAB_INNER_PADDING_X,
      innerPadY: TAB_INNER_PADDING_Y,
    },
  );
}
